import React from 'react';
import {
  Search,
  Target,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  CircleSlash,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';

/**
 * ProfilePillarsCard — shows the 3 LinkedIn pillars (Discoverability /
 * Positioning / Credibility) for URL-imported (scraped) profiles, with
 * attribute-level pass / partial / missing / unknown indicators.
 *
 * If the backend did not compute pillars for this profile (PDF import or
 * thin data), the component renders nothing.
 */
const ICONS = {
  search: Search,
  target: Target,
  'shield-check': ShieldCheck,
};

const STATUS = {
  present: {
    Icon: CheckCircle2,
    label: 'Detected',
    cls: 'text-emerald-600 dark:text-emerald-400',
    pillCls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
  },
  partial: {
    Icon: AlertTriangle,
    label: 'Needs work',
    cls: 'text-amber-600 dark:text-amber-400',
    pillCls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
  },
  missing: {
    Icon: CircleSlash,
    label: 'Missing',
    cls: 'text-destructive',
    pillCls: 'bg-destructive/10 text-destructive border-destructive/25',
  },
  unknown: {
    Icon: HelpCircle,
    label: 'Not visible',
    cls: 'text-muted-foreground',
    pillCls: 'bg-secondary text-muted-foreground border-border',
  },
};

const tone = (score) => {
  if (score >= 75) return 'text-emerald-600 dark:text-emerald-400';
  if (score >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-destructive';
};

export function ProfilePillarsCard({ pillars }) {
  if (!pillars || pillars.available === false) return null;
  const defs = pillars.definitions || [];
  const data = pillars.pillars || {};

  return (
    <Card
      className="rounded-2xl border border-border/70 bg-card/85 backdrop-blur"
      data-testid="profile-pillars-card"
    >
      <CardContent className="p-6 sm:p-7">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-primary">
              LinkedIn Pillars
            </p>
            <h3 className="mt-1 font-display font-semibold text-2xl sm:text-3xl tracking-[-0.02em]">
              Discoverability &middot; Positioning &middot; Credibility
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
              How the three core decisions visitors make about your profile stack up &mdash; with the exact attributes driving each.
            </p>
          </div>
          <Badge variant="outline" className="rounded-full px-3 py-1.5 text-[11px] font-mono-edge tracking-[0.18em] uppercase border-primary/30 text-primary">
            <Sparkles size={11} className="mr-1.5" aria-hidden="true" />
            Profile pillars score: {pillars.overall}
          </Badge>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-3" data-testid="profile-pillars-grid">
          {defs.map((d) => {
            const block = data[d.id];
            if (!block) return null;
            const Icon = ICONS[d.icon] || Search;
            return (
              <div
                key={d.id}
                className="rounded-2xl border border-border/70 bg-background/60 p-5 flex flex-col"
                data-testid={`pillar-${d.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(var(--ep-accent-soft,212_100%_96%))] grid place-items-center text-primary border border-primary/15 shrink-0">
                    <Icon size={18} aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">{d.label}</p>
                    <p className="mt-1 text-[12.5px] text-foreground/80 leading-snug">{d.tagline}</p>
                  </div>
                </div>

                <div className="mt-4 flex items-end gap-2">
                  <span
                    className={`font-display font-semibold text-4xl tracking-[-0.04em] tabular-nums ${tone(block.score)}`}
                    data-testid={`pillar-${d.id}-score`}
                  >
                    {block.score}
                  </span>
                  <span className="pb-1.5 text-xs text-muted-foreground">/ 100</span>
                </div>
                <Progress value={block.score} className="mt-2 h-1.5" />

                <ul className="mt-5 space-y-2.5" data-testid={`pillar-${d.id}-attrs`}>
                  {(block.attributes || []).map((a, i) => {
                    const meta = STATUS[a.status] || STATUS.unknown;
                    const StatusIcon = meta.Icon;
                    return (
                      <li key={i} className="flex items-start gap-2.5">
                        <StatusIcon
                          size={14}
                          className={`mt-0.5 shrink-0 ${meta.cls}`}
                          aria-hidden="true"
                        />
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium tracking-[-0.005em] leading-snug">{a.label}</p>
                          <p className="text-[11.5px] text-muted-foreground leading-snug">{a.note}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="mt-5 text-[11.5px] text-muted-foreground">
          Pillars are derived from your uploaded profile data. Attributes marked &ldquo;Not visible&rdquo; aren&rsquo;t exposed by the audit &mdash; the recommendations still apply.
        </p>
      </CardContent>
    </Card>
  );
}
